import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {CommercialModelConfig, CommercialQuantityType} from '@app/model/dcp-config-service/commercial-model-config';
import {OrderEntryConfiguration} from '@app/model/order/dcp-order-entry.model';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {DcpConfigService} from '@app/services/dcp-config.service';
import {DcpOrderEntryService} from '@app/services/dcp-order-entry.service';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {DcpStartDatePeriod, FeatureTogglesService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CartItemComponent, CartItemContextSource} from '@spartacus/cart/base/components';
import {CartItemContext, CartOutlets} from '@spartacus/cart/base/root';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {CommonConfigurator} from '@spartacus/product-configurator/common';
import {Observable} from 'rxjs';
import {finalize, first} from 'rxjs/operators';
import {
  OrderConfirmationListItemComponentTexts,
  TransactionTypeTexts
} from '../order-confirmation/order-confirmation-list-item/order-confirmation-order-item.model';
import {CheckoutEntryValidationService} from '../services/checkout-entry-validation.service';
import {DcpEntitlementService, DcpTenantKey, DcpTenantsList} from '../services/dcp-entitlement.service';
import {OrderItemService} from './services/order-item.service';
import {Router} from '@angular/router';
import UDExAccordionItem from '@udex/web-components/dist/AccordionItem';

@UntilDestroy()
@Component({
  selector: 'dcp-order-item',
  templateUrl: './order-item.component.html',
  styleUrls: ['./order-item.component.scss'],
  providers: [
    CartItemContextSource,
    {provide: CartItemContext, useExisting: CartItemContextSource},
  ],
  encapsulation: ViewEncapsulation.None,
})
export class OrderItemComponent extends CartItemComponent implements OnInit, OnChanges {
  public tenantsList$: Observable<DcpTenantsList>;
  public tenantKeys: DcpTenantKey[];
  public configurationData: OrderEntryConfiguration;
  public isNetNewTransactionType: boolean;
  public isFirstOrderEntry$: Observable<boolean> = new Observable<boolean>();
  public transactionTypeTexts: TransactionTypeTexts;
  public entryAdditionalLicenses: string = '';
  public isActionProhibited$: Observable<boolean> = this.cartDecorator.isPlaceOrderPending$;
  public entryDuration: DcpStartDatePeriod = {startDate: '', endDate: ''};
  public quantity: number;
  public commercialModelConfig: CommercialModelConfig;
  public isInitialPurchase: boolean = false;
  readonly CartOutlets = CartOutlets;
  @Output() removeAction: EventEmitter<any> = new EventEmitter<any>();
  @Output() editAction: EventEmitter<any> = new EventEmitter<any>();
  @Input() collapsable: boolean = false;
  @Input() componentTexts: OrderConfirmationListItemComponentTexts;
  @Input() derivativeCount: number;
  @Input() isInvalid: boolean = false;
  @Input() showEditAction: boolean = false;
  @Input() showRemoveAction: boolean = false;
  @Input() showPrice: boolean = false;
  public showCpsPriceTooltip: Observable<boolean>;
  private innerOrderEntry: OrderEntry;
  @ViewChild('accordionItem') accordionItem: ElementRef<UDExAccordionItem>;

  constructor(
    public dcpOrderEntryService: DcpOrderEntryService,
    public checkoutEntryValidationService: CheckoutEntryValidationService,
    public orderItemService: OrderItemService,
    protected cartItemContextSource: CartItemContextSource,
    private transactionTypeService: TransactionTypeService,
    private cartDecorator: DcpCartDecoratorService,
    private entitlementService: DcpEntitlementService,
    private dcpConfigService: DcpConfigService,
    private cdr: ChangeDetectorRef,
    private featureToggleService: FeatureTogglesService,
    private router: Router,
  ) {
    super(cartItemContextSource);
  }

  private _collapsed: boolean = false;

  get collapsed(): boolean {
    return this.collapsable && this._collapsed;
  }

  @Input()
  set collapsed(value: boolean) {
    this._collapsed = value && this.orderItemService.getOrderItemCollapsedState(this.orderEntry) !== false;
  }

  get orderEntry(): OrderEntry {
    return this.innerOrderEntry;
  }

  @Input()
  set orderEntry(orderEntry: OrderEntry) {
    this.innerOrderEntry = orderEntry;
    if (orderEntry?.transactionType) {
      this.isInitialPurchase = this.transactionTypeService.isInitialPurchase(orderEntry.transactionType);
      this.transactionTypeService.getTranslations(orderEntry.transactionType)
        .pipe(first(), untilDestroyed(this))
        .subscribe(ttt => this.transactionTypeTexts = ttt);
      this.transactionTypeService.getTransactionTypeByUrlOrOrderEntry(orderEntry)
        .pipe(first(), untilDestroyed(this))
        .subscribe(tt => this.isNetNewTransactionType = this.transactionTypeService.isNetNewTransactionType(tt));
    }
  }

  ngOnInit(): void {
    this.setAdditionalLicenses();
    this.configurationData = this.dcpOrderEntryService.getConfiguration(this.orderEntry);
    this.setDuration(this.configurationData);
    this.getDeploymentSystems();
    this.initCommercialModelConfig();
    this.showCpsPriceTooltip = this.featureToggleService.isEnabled(UNLEASH_TOGGLES.cpsPriceTooltip);
  }

  ngOnChanges(changes?: SimpleChanges) {
    super.ngOnChanges(changes);

    if (changes?.orderEntry) {
      this.cartItemContextSource?.item$.next(this.orderEntry);
      this.entitlementService.updateOrderEntriesList(this.orderEntry);
    }
  }

  setAdditionalLicenses(): void {
    this.entryAdditionalLicenses = this.orderEntry?.quantity?.toString();
  }

  setDuration(configurationData: OrderEntryConfiguration): void {
    if (!configurationData || !configurationData.startDate || !configurationData.endDate) {
      this.entryDuration.startDate = '';
      this.entryDuration.endDate = '';
      return;
    }
    this.entryDuration.startDate = configurationData.startDate;
    this.entryDuration.endDate = configurationData.endDate;
  }

  toggleCollapsed(): void {
    const accordionItemNativeElement = this.accordionItem.nativeElement as UDExAccordionItem;
    this.collapsed = !accordionItemNativeElement.expanded;
    this.orderItemService.setOrderItemCollapsedState(this.orderEntry, this.collapsed);
  }

  getRoute(): string {
    const configuratorType = this.orderEntry.product?.configuratorType;
    return 'configure' + configuratorType;
  }

  getOwnerType(): CommonConfigurator.OwnerType {
    return this.orderEntry.orderCode !== undefined
      ? CommonConfigurator.OwnerType.ORDER_ENTRY
      : CommonConfigurator.OwnerType.CART_ENTRY;
  }



  onEditClick(orderEntry: any): void {
    const route = this.getRoute();
    const ownerType = this.getOwnerType();
    const entityKey = this.orderItemService.getEntityKey(orderEntry);

    this.router.navigate([route], {
      queryParams: {
        ownerType: ownerType,
        entityKey: entityKey,
        displayOnly: false
      }
    });
  }

  private initCommercialModelConfig(): void {
    const product = this.orderEntry?.product;
    const currentBundleTemplate = this.dcpOrderEntryService.getBundleTemplate(this.orderEntry);
    this.dcpConfigService.getCommercialModelConfig(product, this.orderEntry?.transactionType, currentBundleTemplate).pipe(
      first(),
      finalize(() => {
        this.cdr.detectChanges();
      }),
      untilDestroyed(this)
    ).subscribe((commercialModelConfig: CommercialModelConfig) => {
        this.commercialModelConfig = commercialModelConfig;
      },
      () => {
        this.quantity = this.orderEntry?.quantity;
      },
      () => {
        this.quantity = this.commercialModelConfig?.quantityField === CommercialQuantityType.SPEND_AMOUNT ?
          this.configurationData?.spendAmount : this.orderEntry?.quantity;
      });
  }

  private getDeploymentSystems() {
    this.entitlementService.setupTenantSubscription();
    this.tenantsList$ = this.entitlementService.getTenants();
    this.tenantKeys = DcpEntitlementService.getTenantKeys();
  }
}
